import { AvailableInstance, IntegrationsMappedOptions } from '@/types/integrations.types';
import { v4 as uuid } from 'uuid';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import { useState, useCallback } from 'react';
import QueriesMapping from './queries-mapping';
import { fieldObjectToFieldId, fieldsToArray, filterOutValidFields, TempRegField } from '@/utils/integrations';

interface StaticIntegrationMappingProps {
  integration: AvailableInstance;
  overrides?: IntegrationsMappedOptions;
  onChange: ({ staticMapping, valid }: { staticMapping: Record<string, string>; valid: boolean }) => void;
}

export default function StaticIntegrationMapping({
  integration,
  overrides: overrides,
  onChange,
}: StaticIntegrationMappingProps) {
  const [staticFieldsTemp, setStaticFieldsTemp] = useState(
    overrides ? fieldsToArray(overrides.static_mapping ?? {}, '') : fieldsToArray(integration.static_mapping ?? {}, '')
  );
  const [staticFieldErrors, setStaticFieldErrors] = useState<string[]>([]);

  const handleUpdateStaticParam = useCallback(
    (index: number) => (field: string, value: string) => {
      const id = staticFieldsTemp[index].id;
      const fields = staticFieldsTemp.map((item, i) => (index === i ? { field, value, id } : item));
      setStaticFieldsTemp(fields);
      const empties = fields.filter(filterOutValidFields).map<string>(fieldObjectToFieldId);
      setStaticFieldErrors(Array.from(new Set<string>(empties)));
      const staticMapping = Object.fromEntries(fields.map((tempField) => [tempField.field, tempField.value]));
      onChange({
        staticMapping,
        valid: empties.length === 0,
      });
    },
    [staticFieldsTemp, onChange]
  );

  const deleteQuery = useCallback(
    (index: number) => () => {
      const fields = staticFieldsTemp.filter((item, i) => i !== index);
      setStaticFieldsTemp([...fields]);

      // re validate
      const empties = fields.filter(filterOutValidFields).map<string>(fieldObjectToFieldId);
      setStaticFieldErrors(Array.from(new Set<string>(empties)));

      onChange({
        staticMapping: Object.fromEntries(fields.map((tempField) => [tempField.field, tempField.value])),
        valid: empties.length === 0,
      });
    },
    [staticFieldsTemp, onChange]
  );

  const addStaticMapping = () => {
    setStaticFieldsTemp((temp) => [...temp, { field: '', value: '', id: uuid() }]);
  };

  return (
    <div className="static-mapping-integration-container margin-t-16">
      <div className="event-identification">
        <div className="H5">
          <label className="section-title registration-questions-label" id="event-identification-section">
            Static Fields
          </label>
        </div>
        <label className="accent-text">Set static fields to pass with data</label>
      </div>
      <div className="registration-field">
        <div className="grid-row">
          <label className="registration-questions-label grid-row-label" id="registration-questions-label">
            Key
          </label>
          <label id="third-integration-label" className="grid-row-label">
            Value
          </label>
        </div>
        {staticFieldsTemp.map((staticField: TempRegField, index: number) => {
          return (
            <QueriesMapping
              key={'#' + (staticFieldsTemp[index]?.id || uuid())}
              setQuery={handleUpdateStaticParam(index)}
              field={staticField}
              error={staticFieldErrors.includes(staticField.field.toString())}
              deleteQuery={deleteQuery(index)}
            />
          );
        })}
      </div>
      <button className="small" onClick={addStaticMapping}>
        <Icon name={ICONS.ADD} color="var(--gray--200)" size={12} />
        <span className="margin-l-4">Add</span>
      </button>
    </div>
  );
}
